import { useState } from "react";
import type { SocialPost, Blog, Corner } from "../lib/api";
import {
  approveSocial,
  applyLogoToSocial,
  rejectSocial,
  regenerateSocial,
  rescheduleSocial,
  retrySocial,
  resolveCaptionLink,
} from "../lib/api";
import ReviewFlagBanner from "./ReviewFlagBanner";
import LogoCornerPicker from "./LogoCornerPicker";
import Button from "../ui/Button";
import PipelineProgress from "../ui/PipelineProgress";
import { useAsyncAction } from "../ui/useAsyncAction";
import {
  IconClock,
  IconRefresh,
  IconSparkles,
  IconX,
} from "../ui/icons";

function toLocalInputValue(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

export default function SocialPostCard({
  post,
  blog,
  hasLogo,
  onChanged,
}: {
  post: SocialPost;
  blog: Blog | null;
  hasLogo: boolean;
  onChanged: () => void;
}) {
  const [feedback, setFeedback] = useState("");
  const [showFeedback, setShowFeedback] = useState<"image" | "caption" | null>(
    null
  );
  const [scheduleVal, setScheduleVal] = useState(
    toLocalInputValue(post.scheduled_for)
  );

  const canApprove = post.status === "awaiting_approval";
  const angleLabel = (post.angle ?? "").replace(/_/g, " ");
  const resolvedCaption = resolveCaptionLink(
    post.caption,
    {
      short_id: post.short_id,
      angle: post.angle ?? "",
      post_index: post.post_index ?? 0,
    },
    blog ? { slug: blog.slug } : null,
    typeof window !== "undefined" ? window.location.origin : ""
  );

  const approve = useAsyncAction(() => approveSocial(post.id), {
    successMessage: "Post approved",
    onSuccess: onChanged,
  });
  const reject = useAsyncAction(() => rejectSocial(post.id), {
    successMessage: "Post rejected",
    onSuccess: onChanged,
  });
  const regen = useAsyncAction(
    async (stage: "image" | "caption") => {
      await regenerateSocial(post.id, stage, feedback || undefined);
    },
    {
      successMessage: "Regenerating",
      successDescription: "The card will update as the stage finishes.",
      onSuccess: () => {
        setShowFeedback(null);
        setFeedback("");
        onChanged();
      },
    }
  );
  const saveSchedule = useAsyncAction(
    async () => {
      if (!scheduleVal) throw new Error("Pick a date and time first");
      const d = new Date(scheduleVal);
      if (Number.isNaN(d.getTime())) throw new Error("That date is invalid");
      await rescheduleSocial(post.id, d.toISOString());
    },
    { successMessage: "Schedule updated", onSuccess: onChanged }
  );
  const retry = useAsyncAction(() => retrySocial(post.id), {
    loadingMessage: "Retrying publish…",
    successMessage: "Retry queued",
    onSuccess: onChanged,
  });
  const applyLogo = useAsyncAction(
    (corner: Corner | null) => applyLogoToSocial(post.id, corner),
    {
      successMessage: "Applying logo",
      successDescription: "The composite will appear in a few seconds.",
      onSuccess: () => setTimeout(onChanged, 2500),
    }
  );

  const scheduleChanged =
    scheduleVal !== toLocalInputValue(post.scheduled_for);

  return (
    <div className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm shadow-ink-900/5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-widest text-brand-700">
            #{(post.post_index ?? 0) + 1} · {angleLabel}
          </span>
        </div>
      </div>

      <PipelineProgress
        kind="social"
        status={post.status}
        reviewPassed={post.review_passed}
        variant="compact"
        className="mb-4"
      />

      <div className="flex gap-4 flex-col sm:flex-row">
        {post.image_url && (
          <div className="shrink-0 flex flex-col gap-2">
            <img
              src={post.image_url}
              alt=""
              className="rounded-lg aspect-square object-cover w-full sm:w-48 h-48 bg-ink-100 border border-ink-100"
            />
            <LogoCornerPicker
              currentCorner={post.image_logo_corner}
              hasLogo={hasLogo}
              onApply={async (corner) => {
                await applyLogo.run(corner);
              }}
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          {resolvedCaption && (
            <p className="text-sm text-ink-800 whitespace-pre-wrap leading-relaxed">
              {resolvedCaption}
            </p>
          )}
        </div>
      </div>

      {/* Schedule editor */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <label className="inline-flex items-center gap-1.5 text-xs text-ink-500">
          <IconClock size={12} />
          Scheduled for
        </label>
        <input
          type="datetime-local"
          value={scheduleVal}
          onChange={(e) => setScheduleVal(e.target.value)}
          className="text-sm px-2 py-1 border border-ink-200 rounded-md focus:border-brand-500"
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => saveSchedule.run()}
          disabled={!scheduleChanged}
          pending={saveSchedule.pending}
        >
          {saveSchedule.pending ? "Saving…" : "Save"}
        </Button>
      </div>

      <div className="mt-4">
        <ReviewFlagBanner
          flags={post.review_flags}
          passed={post.review_passed}
        />
      </div>

      <div className="flex gap-2 mt-4 flex-wrap">
        {canApprove && (
          <>
            <Button
              variant="primary"
              size="sm"
              onClick={() => approve.run()}
              pending={approve.pending}
            >
              {approve.pending ? "Approving…" : "Approve"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => reject.run()}
              pending={reject.pending}
            >
              {reject.pending ? "Rejecting…" : "Reject"}
            </Button>
          </>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowFeedback("caption")}
          iconLeft={<IconRefresh size={12} />}
        >
          Regenerate caption
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowFeedback("image")}
          iconLeft={<IconRefresh size={12} />}
        >
          Regenerate image
        </Button>
        {post.status === "failed" && (
          <Button
            variant="dark"
            size="sm"
            onClick={() => retry.run()}
            pending={retry.pending}
            iconLeft={<IconSparkles size={12} />}
          >
            {retry.pending ? "Retrying…" : "Retry publish"}
          </Button>
        )}
      </div>

      {showFeedback && (
        <div className="mt-4 rounded-lg border border-ink-100 bg-sand-50 p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-ink-700">
              Regenerate {showFeedback}
            </p>
            <button
              onClick={() => {
                setShowFeedback(null);
                setFeedback("");
              }}
              className="text-ink-400 hover:text-ink-700"
            >
              <IconX size={14} />
            </button>
          </div>
          <textarea
            placeholder="Optional guidance for the regeneration…"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            className="w-full p-2 border border-ink-200 rounded-md text-sm focus:border-brand-500 resize-none"
            rows={3}
          />
          <div className="flex gap-2 mt-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => regen.run(showFeedback)}
              pending={regen.pending}
              iconLeft={<IconRefresh size={12} />}
            >
              {regen.pending ? "Queuing…" : `Regenerate ${showFeedback}`}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowFeedback(null);
                setFeedback("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {post.error_message && post.status === "failed" && (
        <p className="mt-3 text-sm text-red-700">{post.error_message}</p>
      )}
    </div>
  );
}
